import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import ReactDOM from 'react-dom'

// Above the Drawer panel (z-1050, cf. ui/Drawer) so a modal opened from inside
// A drawer — and its backdrop — render on top instead of behind the panel.
const MODAL_Z = 1060
const BACKDROP_Z = 1055

// `md` matches the DS warning/confirmation modal width (Figma 448px = max-w-md);
// `!` beats Bootstrap's media-query max-width on `.modal-dialog`.
const DIALOG_SIZE = { sm: 'modal-sm', compact: 'tw:max-w-100! tw:mt-10!', md: 'tw:max-w-md!', lg: 'modal-lg' }

const Modal = ({
  isOpen, onClose, title, children, footer = null, header = null, size = 'lg',
  contentClassName = 'tw:rounded!', bodyClassName = 'p-4', footerClassName = 'p-3', backdropClassName = ''
}) => {
  const [show, setShow] = useState(false)
  const [display, setDisplay] = useState(false)

  // Clear the pending animation timer on unmount / re-toggle, otherwise it
  // Fires setState after teardown and leaks across tests.
  useEffect(() => {
    if (isOpen) {
      setDisplay(true)
      const timer = setTimeout(() => setShow(true), 10)
      return () => clearTimeout(timer)
    }
    setShow(false)
    const timer = setTimeout(() => setDisplay(false), 300)
    return () => clearTimeout(timer)
  }, [isOpen])

  /*
   * Lock background scroll while open. `overflow: hidden` on body is enough
   * On desktop and — crucially — preserves the coordinate system used by
   * Portaled menus (e.g. react-select menuPortalTarget=document.body) which
   * Break when body is set to position: fixed.
   */
  useEffect(() => {
    if (isOpen)
      document.body.style.overflow = 'hidden'
    else
      document.body.style.overflow = ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const handleBackdropClick = useCallback(event => {
    // Only close if clicking directly on the modal wrapper, not its children
    if (event.target.classList.contains('modal'))
      onClose()
  }, [onClose])

  if (!display)
    return null

  const defaultHeader = (
    <div className="modal-header">
      <h5 className="modal-title">
        {title}
      </h5>
      <button
        aria-label="Fermer la modale"
        className="close"
        type="button"
        onClick={onClose}
      >
        <i aria-hidden="true" className="fas fa-times" />
      </button>
    </div>
  )

  const modalContent = (
    <>
      <div
        className={`modal fade ${show ? 'show' : ''}`}
        style={{ display: 'block', zIndex: MODAL_Z }}
        tabIndex="-1"
        onClick={handleBackdropClick}
      >
        <div className={`modal-dialog ${DIALOG_SIZE[size]} modal-dialog-scrollable`}>
          <div className={`modal-content ${contentClassName}`}>
            {header || defaultHeader}
            <div className={`modal-body ${bodyClassName}`}>
              {children}
            </div>
            {footer ? (
              <div className={`modal-footer ${footerClassName}`}>
                {footer}
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <div
        className={`modal-backdrop fade ${show ? 'show' : ''} ${backdropClassName}`}
        style={{ zIndex: BACKDROP_Z }}
      />
    </>
  )

  return ReactDOM.createPortal(modalContent, document.body)
}

Modal.propTypes = {
  backdropClassName: PropTypes.string,
  bodyClassName: PropTypes.string,
  children: PropTypes.node.isRequired,
  contentClassName: PropTypes.string,
  footer: PropTypes.node,
  footerClassName: PropTypes.string,
  header: PropTypes.node,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  size: PropTypes.oneOf(['sm', 'compact', 'md', 'lg']),
  title: PropTypes.node.isRequired
}

Modal.defaultProps = {
  backdropClassName: '',
  bodyClassName: 'p-4',
  contentClassName: 'tw:rounded!',
  footer: null,
  footerClassName: 'p-3',
  header: null,
  size: 'lg'
}

export default Modal
